import React, {
    useRef,
    useMemo,
    useState,
    useEffect,
    useContext,
    useCallback
} from "react";
import {
    Page,
    pdfjs,
    Document
} from "react-pdf";
import type {
    PDFDocumentProxy
} from "pdfjs-dist";
import debounce from "lodash/debounce";
import stylesXplrr from "./XplrrBOXPDF.module.css";
import { TbBoxAlignLeft } from "react-icons/tb";
import { IconButton, TooltipHost, DirectionalHint  } from "@fluentui/react";
import { ImFilesEmpty } from "react-icons/im";
import { MdErrorOutline } from "react-icons/md";
import {
    FiZoomIn,
    FiZoomOut
} from "react-icons/fi";
import { GlobalContext } from "../../GlobalContext";
import { ProgressIndicator } from "@fluentui/react/lib/ProgressIndicator";
import {
    findRange,
    contentPath,
    pdfCacheKey,
    getPagesDims,
    setGGlobalDims,
    areAllFlagsTrue,
    getDesiredWidth,
    getDesiredHeight,
    getClassClientWidth,
    saveEntriesToGlobal,
    storeRescaleWidth
} from "../../util_glob";
import {
    gfile,
    gpdftextitem,
    gpagedimensions,
    gautoparagraph,
    gautoparagraphresult,
    get_global_file_cache_empty
} from "../../interfaces";
import { get_mod_str } from "../../util_glob";
import { pdfSourceCodes } from "../../lsts";
import { tooltipZoomMngPdfDedicatedStyles, tooltipZoomMngPdfStyles, zoomInOutViewer } from "../../styles_glob";
import { qGetFileParagraps } from "../../api/api";
import { ParagraphOverlay } from "../ParagraphOverlayFX/ParagraphOverlay";

import {
    saveItem,
    loadItem,
    removeItem
} from "../../storage";
import { TbArrowAutofitWidth } from "react-icons/tb";
import ParagraphOverlayPulseButton from "../ParagraphOverlayPulseButton/ParagraphOverlayPulseButton";


pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

(pdfjs.GlobalWorkerOptions as any).useSystemFonts = true;
(pdfjs.GlobalWorkerOptions as any).disableFontFace = false;
(pdfjs.GlobalWorkerOptions as any).fontExtraProperties = true;

let timeoutId: number;

interface PDFBoxDetails {
    file: gfile | undefined;
    handleTimeout: () => void;
    handleScroll: (pageNumCur: number) => void;
    setPages: (numPages: number) => void;
    setContentList: (contentList: { title: string; dest: number }[]) => void;
    startPage: number;
    activeFileId: string;
    callBackVisibleEntries: (vis: number[]) => void;
    callBackIsLoading: (isL: boolean) => void;
    srcLoc: string;
    companyId: string;
    panelWidthRight?: number | string;
    resizeTriggerBack: (width: number) => void;
    isResizing: boolean;
    shouldBeInWideViewMode?: boolean;
    parentClassName: string;
}

interface PDFPageDetails {
    handleTimeout: () => void;
    setContentList: (contentList: { title: string; dest: number }[]) => void;
    nums: number;
    startPage: number;
    fileId: string;
    fileIndexChunks: number[];
    filePageDimsState: gpagedimensions[] | [];
    rescaleWidthParam?: number;
}

const XplrrBOXPDF = ({
    file,
    handleTimeout,
    handleScroll,
    setPages,
    setContentList,
    startPage,
    activeFileId,
    callBackVisibleEntries,
    callBackIsLoading,
    srcLoc,
    companyId,
    panelWidthRight,
    resizeTriggerBack,
    isResizing,
    shouldBeInWideViewMode,
    parentClassName
}: PDFBoxDetails) => {
    const { fileList, loggedInUser, docAudio, selectedGroup } = useContext(GlobalContext);
    const [numPages, setNumPages] = useState<number>(0);
    const [pgs, setPgs] = useState<React.ReactElement<any, any>[]>([]);
    const [isLoa, setIsLoa] = useState<boolean>(true);
    const [pageN, setPageN] = useState<number>(startPage);
    const [fileId, setFileId] = useState<string>(activeFileId);
    const [scrollTr, setScrollTr] = useState<string>("");
    const [progress, setProgress] = useState<number>(0);
    const [loadMark, setLoadMark] = useState<string>();
    const [resizeCount, setResizeCount] = useState<number>(0);
    const [rescaleWidth, setRescaleWidth] = useState<number>(getDesiredWidth(srcLoc, selectedGroup.selectionId, true));
    const [manualResizeWidth, setManualResizeWidth] = useState<number>(getDesiredWidth(srcLoc, selectedGroup.selectionId, true));
    const [isBlobPulling, setIsBlobPulling] = useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = useState<string>(activeFileId);
    const [onDemandRefresh, setOnDemandRefresh] = useState<number>(0);
    const [fileIndexChunks, setFileIndexChunks] = useState<number[]>([]);
    const [filePageDimsState, setFilePageDimsState] = useState<gpagedimensions[] | []>([]);
    const [showParagraphOverlay, setShowParagraphOverlay] = useState<boolean>(false);

    const pageRefs = useRef<(Element | null)[]>([]);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const containerRef = useRef(null);
    const pageSizeRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
    const paragraphsRef = useRef<gautoparagraphresult[]>([]);


    useEffect(() => {
        window.paragraphOverlayOn = showParagraphOverlay;
    }, [showParagraphOverlay]);

    useEffect(() => {
        const handleResize = debounce(() => {
            setResizeCount(prevCount => prevCount + 1);
        }, 100);
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            handleResize.cancel();
        };
    }, []);

    useEffect(() => {
        if (!fileId || !selectedGroup?.selectionId) return;
        qGetFileParagraps(fileId, loggedInUser).then((res: gautoparagraphresult[]) => {
            paragraphsRef.current = res;
        });
    }, [fileId, selectedGroup?.selectionId]);

    useEffect(() => {
        if (!activeFileId) return;

        let visibleEntries: number[] = [];
        let cacheEntries: number[] = [];

        observerRef.current = new IntersectionObserver(
            entries => {
                let currentEntries: number[] = [];
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const pageNumber = parseInt((entry.target as HTMLElement).dataset.pageNumber || "0", 10);
                        if (!visibleEntries.includes(pageNumber)) visibleEntries.push(pageNumber);

                        currentEntries.push(pageNumber);
                    }
                });

                window.globalIntersectingEntries = currentEntries;

                visibleEntries = visibleEntries.sort((a, b) => a - b);

                if (currentEntries.length < 1) currentEntries = cacheEntries;

                visibleEntries = currentEntries;
                const minVisible = Math.min(...visibleEntries);
                const maxVisible = Math.max(...visibleEntries);

                for (let i = 1; i <= 5; i++) {
                    visibleEntries.push(maxVisible + i);
                    visibleEntries.push(minVisible - i);
                }
                if (currentEntries.length > 0) cacheEntries = currentEntries;

                callBackVisibleEntries(visibleEntries);
                saveEntriesToGlobal(currentEntries);
            },
            { root: containerRef.current, rootMargin: "1000px 0px" }
        );

        pageRefs.current.forEach(ref => {
            if (ref) observerRef?.current?.observe(ref);
        });

    }, [activeFileId]);

    useEffect(() => {
        let parentWidth = getClassClientWidth(parentClassName);

        if(srcLoc != "sbs"){
            if (parentWidth > 0) setRescaleWidth((parentWidth) - 8);
        }  else {
            // setRescaleWidth(((window.innerWidth * 0.55) - 8));
        }

    }, [resizeCount]);

    useEffect(() => {
        callBackIsLoading(true);
        setLoadMark(`${file ? "fileY" : "fileN"}_${activeFileId}_${startPage}_${window.globalBase64String?.length}`);

        if (!file) return;

        if (file.indexchunks && startPage) {
            setFileIndexChunks(findRange(file.indexchunks, startPage));
        } else setFileIndexChunks([startPage - 1]);

        const cacheBase64Wndw = async (fl: gfile): Promise<boolean> => {

            if (!fl || !fl.fileid || !fl.modified) return false;

            setIsBlobPulling(true);

            if (window.globalPDFCache === undefined) window.globalPDFCache = get_global_file_cache_empty();
            if (window.globalPreLoadMap === undefined) window.globalPreLoadMap = new Map<string, string>();

            const cacheKey = pdfCacheKey(fl.fileid, fl.modified);
            const flPath = contentPath(fl.name, fl.fileid);

            // if file loading right now ⇒ return
            if (!window.globalPDFCache.has(cacheKey) && window.globalPreLoadMap.has(cacheKey)) return false;

            if (window.globalPDFCache.has(cacheKey)) {
                setLoadingMessage(`CONTENT FETCH ${Math.round(parseInt(fl.size) / (1024 * 1024))}MB`);

                const cacheVal = window.globalPDFCache.get(cacheKey)?.base64string;
                if (!cacheVal || cacheVal == "" || cacheVal == undefined) {
                    handleNoValidCacheVal(cacheKey, fl);
                    setGGlobalDims(fl.pagedimensions, srcLoc, selectedGroup.selectionId);
                    return false;
                }

                let pgDms = getPagesDims(cacheKey, fl.pagedimensions, window.globalPDFCache.get(cacheKey)?.pageDimensions);
                setGGlobalDims(pgDms, srcLoc, selectedGroup.selectionId);

                window.globalBase64String = `data:application/pdf;base64,${cacheVal}`;

                setOnDemandRefresh(prevNum => prevNum + 1);

                window.globalBase64StringId = cacheKey;

                setProgress(1);

                return true;

            } else {
                let dCache = await loadItem(cacheKey);

                if (dCache &&  dCache.base64string && dCache.base64string != "" && dCache.base64string != undefined) {

                    const cacheVal = dCache?.base64string;
                    if (!cacheVal || cacheVal == '' || cacheVal == undefined) {
                        handleNoValidCacheVal(cacheKey, fl);
                        setGGlobalDims(fl.pagedimensions, srcLoc, selectedGroup.selectionId);
                        return false;
                    }

                    let pgDms = getPagesDims(cacheKey, fl.pagedimensions, dCache?.pageDimensions);
                    setGGlobalDims(pgDms, srcLoc, selectedGroup.selectionId);

                    window.globalBase64String = `data:application/pdf;base64,${cacheVal}`;

                    setOnDemandRefresh(prevNum => prevNum + 1);

                    window.globalBase64StringId = cacheKey;

                    window.globalPDFCache.set(cacheKey, dCache);

                    setProgress(1);

                    return true;

                } else {
                    setGGlobalDims(fl.pagedimensions, srcLoc, selectedGroup.selectionId);
                }

                window.globalPreLoadMap.set(cacheKey, cacheKey);

                const params = new URLSearchParams({
                    cachemodstring: get_mod_str(fl.modified)
                });

                let flsize = Math.round(parseInt(fl.size) / (1024 * 1024));

                setLoadingMessage(`INITIAL LOAD ${flsize}MB ${flsize > 5 ? "PLEASE WAIT..." : ""}`);

                const base64 = await downloadFile(`/content/${companyId}/${flPath}` + `?${params.toString()}`);

                if (!base64 || base64 == "" || base64 == undefined) {
                    window.globalPDFCache.delete(cacheKey);
                    window.globalPreLoadMap.delete(cacheKey);
                    removeItem(cacheKey);
                    return false;
                }

                window.globalBase64String = `data:application/pdf;base64,${base64}`;
                window.globalBase64StringId = cacheKey;

                window.globalPDFCache.set(cacheKey, {
                    base64string: `${base64}`,
                    fileid: fl.fileid,
                    name: fl.name,
                    modified: fl.modified,
                    pageContent: fl.pagecontent,
                    pageDimensions: fl.pagedimensions
                });

                saveItem(cacheKey, {
                    base64string: `${base64}`,
                    fileid: fl.fileid,
                    name: fl.name,
                    modified: fl.modified,
                    pageContent: fl.pagecontent,
                    pageDimensions: fl.pagedimensions
                });

                setIsBlobPulling(false);
                setIsLoa(false);
                setProgress(1);
                setLoadMark(`${file ? "fileY" : "fileN"}_${activeFileId}_${startPage}_${window.globalBase64String?.length}`);
                setOnDemandRefresh(prevNum => prevNum + 1);

                return true;
            }
        };

        const handleNoValidCacheVal = (cacheKey: string, fl: gfile) => {
            removeItem(cacheKey);
            window.globalPDFCache.delete(cacheKey);
            window.globalPreLoadMap.delete(cacheKey);
            try {
                console.log("‹cache_try_again_01›");
                cacheBase64Wndw(fl);
            } catch (e) {
                import.meta.env.DEV && console.log("‹errr_cachebase64wndw›", e);
            }
        };

        if (activeFileId != null && activeFileId != "" && activeFileId != undefined) {

            cacheBase64Wndw(file).then((opResult: boolean) => {
                if (opResult == true) {
                    setFileId(activeFileId);
                    setIsLoa(false);
                }
                callBackIsLoading(false);
            });

        } else {
            callBackIsLoading(false);
        }
    }, [activeFileId]);

    const handleScrollCallAdv = (event?: any) => {
        if (event) {
            let { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
            handleScroll(getCurrentPage(filePageDimsState, scrollTop, clientHeight));
        } else {
            if (containerRef.current) handleScroll(getCurrentPage(filePageDimsState, window.scrollY || 0, window.innerHeight || 0));
        }
        debounce(() => {
            setScrollTr(new Date().toISOString());
        }, 50)();
    };

    useEffect(() => {
        handleScrollCallAdv();
    }, [window.global_wiggle_stamp]);

    useEffect(() => {
        import.meta.env.DEV === true && console.log(`‹pfd›`);
    }, [pageN, fileId]);

    const addToObserver = useCallback((ref: any, index: number) => {
        if (ref) {
            pageRefs.current[index] = ref;
            ref.dataset.pageNumber = (index + 1).toString();
            if (observerRef.current) observerRef.current.observe(ref);
        }
    }, []);

    const startProgressBump = useCallback(() => {
        const bumpProgress = () => {
            setProgress(prevProgress => {
                const currentProgress = prevProgress || 0;
                const newProgress = currentProgress + 0.001;

                // continue bumping if we're still in control, but stop when external progress takes over
                if (newProgress < 0.1) setTimeout(bumpProgress, 1000);

                return newProgress;
            });
        };

        bumpProgress();
    }, []);

    const preparePdfPageCallBack = useCallback(({ handleTimeout, nums, setContentList, startPage, fileId, fileIndexChunks, filePageDimsState, rescaleWidthParam }: PDFPageDetails) => {
        const pages = [];
        window.globalItemCounts = new Map<number, { count: number; flag: boolean }>();

        let resizeWidthDynamic = rescaleWidth;
        if(rescaleWidthParam && rescaleWidthParam > 0) resizeWidthDynamic = rescaleWidthParam;
        for (let i = 0; i < nums; i++) {

            pages.push(
                <Page
                    key={`pg_${i + 1}`}
                    pageNumber={i + 1}
                    style={{ overflowY: true, behavior: "smooth" }}
                    width={resizeWidthDynamic}
                    loadingText={<div className={stylesXplrr.loadingTextMain}>LDNG</div>}
                    noData={<div className={stylesXplrr.loadingTextMain}>LDNG</div>}
                    onGetTextSuccess={(items: any, styles: any) => {
                        // prepare page item counts for flagging
                        window.globalItemCounts.set(i, {
                            count: items.items.length,
                            flag: false
                        });
                    }}
                    customTextRenderer={({ pageIndex, pageNumber, itemIndex, str }: gpdftextitem) => {
                        // if item is last on page set flag to true
                        if (itemIndex == (window.globalItemCounts.get(i)?.count || 0) - 1) {
                            window.globalItemCounts.set(i, {
                                count: window.globalItemCounts.get(i)?.count || 0,
                                flag: true
                            });
                            // if all page, page ready flags are true, set loaded to true
                            if (areAllFlagsTrue(window.globalItemCounts, fileIndexChunks)) {
                                if (window.globalAllNessesaryPDFPagesLoaded == false) {
                                    window.globalAllNessesaryPDFPagesLoaded = true;
                                    try {
                                        clearTimeout(timeoutId); // Clear previous timeout
                                    } catch {
                                        import.meta.env.DEV && console.log("‹no_timeout_to_clear›");
                                    }
                                    timeoutId = setTimeout(handleTimeout, 100) as unknown as number;
                                }
                            }
                        }
                        return `<span id='CSP_${pageIndex}_${itemIndex}' class='kjgi pdf_itm_${pageIndex}'>${str}</span>`;
                    }}
                />
            );
        }
        return pages;
    }, [manualResizeWidth]);

    const pdfOptions = useMemo(
        () => ({
            cMapUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/",
            cMapPacked: true,
            standardFontDataUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/standard_fonts/",
            fontExtraProperties: true,
            useSystemFonts: true
        }),
        []
    ); // empty dependency array since these values don't change

    const renderedPageWidths = useMemo(() => {
        let resizeWidthDynamic = rescaleWidth;
        if(manualResizeWidth > 0) resizeWidthDynamic = manualResizeWidth;
        return filePageDimsState?.map((dim: gpagedimensions, index: number) => {
            return {
                width: resizeWidthDynamic,
                height: getDesiredHeight(dim, srcLoc, selectedGroup.selectionId, resizeWidthDynamic)
            }
        });
    }, [rescaleWidth, manualResizeWidth, filePageDimsState, window.innerWidth]);

    const renderedPages = useMemo(() => {
        if (!filePageDimsState) return null;

        return filePageDimsState.map((dim: gpagedimensions, index: number) => {
            return (
                (index > startPage - (window.globalpdfObservableRange || 5) && index < startPage + (window.globalpdfObservableRange || 5)) ||
                (window.globalUserScrollLazyLoadEnabled === true && window.globalCurrentVisibleEntries?.includes(index)) ? (
                    <div className={stylesXplrr.pdfPageWrap} key={`${index}_wrp`}>
                        <div
                            key={`${index}_page`}
                            className={stylesXplrr.pdfPageSubWrap}
                            data-load-mark={loadMark}
                            data-p-type={"page"}
                            ref={(ref: any) => {
                                addToObserver(ref, index);
                                pageSizeRefs.current[index] = ref;
                            }}
                            style={{
                                width: renderedPageWidths?.[index]?.width,
                                height: renderedPageWidths?.[index]?.height
                            }}
                        >
                            {pgs[index]}
                        </div>
                        <ParagraphOverlay
                            paragraphs={paragraphsRef.current}
                            pagedimensions={filePageDimsState}
                            pageNumber={index}
                            pageWidth={renderedPageWidths?.[index]?.width || 0}
                            pageHeight={renderedPageWidths?.[index]?.height || 0}
                            visible={showParagraphOverlay}
                        />
                    </div>
                ) : (
                    <div
                        key={`${index}_mock`}
                        className={stylesXplrr.pdfPageSubWrap}
                        data-load-mark={loadMark}
                        data-p-type={"mock"}
                        ref={(ref: any) => addToObserver(ref, index)}
                        style={{
                            width: renderedPageWidths?.[index]?.width,
                            height: renderedPageWidths?.[index]?.height
                        }}
                    ></div>
                )
            );

        });
    }, [
        filePageDimsState,
        startPage,
        window.globalpdfObservableRange,
        window.globalUserScrollLazyLoadEnabled,
        window.globalCurrentVisibleEntries,
        rescaleWidth,
        addToObserver,
        getDesiredHeight,
        loadMark,
        pgs,
        srcLoc,
        window.globalWigglePixel,
        window.globalHiglightFound,
        window.global_wiggle_stamp,
        showParagraphOverlay,
        renderedPageWidths
    ]);

    const handleLoadSuccess = async (pdfObject: PDFDocumentProxy) => {
        const numPages = pdfObject.numPages;
        window.globalNumPages = numPages;

        const pgs = preparePdfPageCallBack({
            handleTimeout: handleTimeout,
            setContentList: setContentList,
            startPage: startPage,
            nums: numPages,
            fileId: fileId,
            fileIndexChunks: fileIndexChunks,
            filePageDimsState: filePageDimsState
        });

        setPgs(pgs);

        setFilePageDimsState(window.globalPDFPageDimensions?.map((dim, index) => ({ ...dim, scaledHeight: getDesiredHeight(dim, srcLoc, selectedGroup.selectionId) })));
        setOnDemandRefresh(prevNum => prevNum + 1);
        setTimeout(() => {
            setOnDemandRefresh(prevNum => prevNum + 1);

            // 🧧Auto-show paragraph overlay if not in "sbs" mode
            srcLoc != "sbs" && setShowParagraphOverlay(true);
        }, 100);
        setNumPages(numPages);
        setPages(numPages);
    };

    const downloadFile = async (url: string): Promise<string | null> => {
        try {
            setProgress(0.001);
            startProgressBump()
            setTimeout(() => {
                setLoadingMessage("DOWNLOADING ON DEVICE");
            }, 1000);

            const response = await fetch(url);
            if (response && response.body) {
                const reader = response.body.getReader();
                const contentLengthHeader = response.headers.get("kgnfcl");
                const contentLength = contentLengthHeader ? parseInt(contentLengthHeader, 10) : 1;
                let receivedLength = 0;
                const chunks: Uint8Array[] = [];

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    chunks.push(value);
                    receivedLength += value.length;
                    setProgress(receivedLength / contentLength);

                    setLoadingMessage(prevMessage => prevMessage.replace("INITIAL LOAD", "DOWNLOADING ON DEVICE"));
                }
                const allChunks = new Uint8Array(receivedLength);
                let position = 0;
                for (const chunk of chunks) {
                    allChunks.set(chunk, position);
                    position += chunk.length;
                }
                const chunkSize = 0x8000; // 32KB chunks
                let base64 = "";
                for (let i = 0; i < allChunks.length; i += chunkSize) {
                    const chunk = allChunks.subarray(i, i + chunkSize);
                    base64 += String.fromCharCode.apply(null, chunk as unknown as number[]);
                }
                return btoa(base64);
            }
        } catch (err) {
            console.error("download_failed:", err);
        }
        return null;
    };

    const getCurrentPage = (pageDimensions: gpagedimensions[], scrollTop: number, clientHeight: number): number => {
        let cumulativeHeight = 0;
        const middleOfViewport = scrollTop + clientHeight / 2;
        for (let i = 0; i < pageDimensions.length; i++) {
            cumulativeHeight += getDesiredHeight(pageDimensions[i], srcLoc, selectedGroup.selectionId, rescaleWidth);
            if (cumulativeHeight > middleOfViewport) return i + 1; // +1 because page numbers typically start at 1, not 0
        }
        return pageDimensions.length; // last page
    };

    return (
        <div id="pdf-viewer" className={stylesXplrr.pdfViewer}>
            {/* Zoom Buttons */}
            <div
                style={{ position: 'absolute', top: '-1px',  display: 'flex', alignItems: 'center', width: panelWidthRight, justifyContent: 'flex-end' }}
                className={srcLoc == "sbs" ? stylesXplrr.controlContainerSBS : stylesXplrr.controlContainer}
            >
                {srcLoc != "sbs" && (
                    <IconButton
                        className={srcLoc == "sbs" ? stylesXplrr.zoomInButtonSBS : stylesXplrr.zoomInButton}
                        styles={zoomInOutViewer}
                        disabled={rescaleWidth >= window.innerWidth - 10}
                        onClick={() => {
                            const nextWidth = Math.min(rescaleWidth + 100, window.innerWidth);
                            if (nextWidth <= rescaleWidth) return;
                            setManualResizeWidth(nextWidth);
                            storeRescaleWidth(selectedGroup.selectionId, nextWidth);
                            const pgs = preparePdfPageCallBack({
                                handleTimeout: handleTimeout,
                                setContentList: setContentList,
                                startPage: startPage,
                                nums: numPages,
                                fileId: fileId,
                                fileIndexChunks: fileIndexChunks,
                                filePageDimsState: filePageDimsState,
                                rescaleWidthParam: nextWidth
                            });

                            setPgs(pgs);

                            setRescaleWidth(nextWidth);
                            setResizeCount(prev => prev + 1);
                        }}
                        >
                        <FiZoomIn size={20}/>
                    </IconButton>
                )}
                {srcLoc != "sbs" && (
                    <IconButton
                        className={srcLoc == "sbs" ? stylesXplrr.zoomOutButtonSBS : stylesXplrr.zoomOutButton}
                        styles={zoomInOutViewer}
                        disabled={rescaleWidth <= 460}
                        onClick={() => {
                            const nextWidth = Math.max(rescaleWidth - 100, 450);
                            if (nextWidth >= rescaleWidth) return;
                            setManualResizeWidth(nextWidth);
                            storeRescaleWidth(selectedGroup.selectionId, nextWidth);
                            const pgs = preparePdfPageCallBack({
                                handleTimeout: handleTimeout,
                                setContentList: setContentList,
                                startPage: startPage,
                                nums: numPages,
                                fileId: fileId,
                                fileIndexChunks: fileIndexChunks,
                                filePageDimsState: filePageDimsState,
                                rescaleWidthParam: nextWidth
                            });

                            setPgs(pgs);

                            setRescaleWidth(nextWidth);
                            setResizeCount(prev => prev + 1);
                        }}
                        >
                        <FiZoomOut size={20} />
                    </IconButton>
                )}
                {srcLoc != "sbs" && (
                    <TooltipHost directionalHint={DirectionalHint.bottomCenter}
                        styles={tooltipZoomMngPdfDedicatedStyles}
                        content={
                            showParagraphOverlay ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', borderLeft: '4px solid #ffcc00', paddingLeft: 4, gap: 4, fontFamily: "Raleway, Urbanist, Roboto, sans-serif"  }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', paddingLeft: 4, fontSize: 12, fontWeight: 600, color: '#333' }}>
                                        Paragraph Overlay Enabled & Drag-to-Select Enabled
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', marginTop: 4, fontSize: 10, color: '#666' }}>
                                    Each Text element lights up on mousehover and can be copied with click. Drag a square to select multiple elements. They are auto-copied on mouse left-button release.
                                    </div>
                                </div>
                            )
                            : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', borderLeft: '4px solid #ffcc00', paddingLeft: 4, gap: 6, fontFamily: "Raleway, Urbanist, Roboto, sans-serif"  }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', paddingLeft: 4, fontSize: 12, fontWeight: 600, color: '#333' }}>
                                        Show Paragraph Overlay & Enable Drag-to-Select
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', paddingLeft: 4, marginTop: 4, fontSize: 10, color: '#666' }}>
                                        Each Text element lights up on mousehover and can be copied with click. Drag a square to select multiple elements. They are auto-copied on mouse left-button release.
                                    </div>
                                </div>
                            )
                        }
                    >
                        <ParagraphOverlayPulseButton
                            showParagraphOverlay={showParagraphOverlay}
                            setShowParagraphOverlay={setShowParagraphOverlay}
                            zoomInOutViewer={zoomInOutViewer}
                            buttonClassName={`${stylesXplrr.zoomOutButton} ${stylesXplrr.showParagraphOverlay}`}
                        />
                    </TooltipHost>
                )}
                {srcLoc == "sbs" && (
                    <div className={stylesXplrr.mockFeedbackContainer}>
                    </div>
                )}
                {docAudio && (
                    <div className={stylesXplrr.mockAudioContainer}>
                    </div>
                )}
                <div className={stylesXplrr.mockCloseButton}>
                </div>
            </div>

            {fileList.length == 0 ? (
                <div className={stylesXplrr.noDocs}>
                    <div className={stylesXplrr.iconNoContent}>
                        <ImFilesEmpty size={24} />
                    </div>
                    <div>Space Empty. Upload documents from the Management Tab</div>
                </div>
            ) : progress && progress < 1 ? (
                <div className={stylesXplrr.loadingIndicatorContainer}>
                    <div
                        key={`loa${progress}`}
                        className={`${
                            srcLoc == pdfSourceCodes.modal
                                ? stylesXplrr.loadingIndicator
                                : srcLoc == pdfSourceCodes.sbs
                                ? stylesXplrr.loadingIndicatorStatic
                                : srcLoc == pdfSourceCodes.inv && file?.invoicedata != null
                                ? stylesXplrr.loadingIndicatorInv
                                : srcLoc == pdfSourceCodes.inv
                                ? stylesXplrr.loadingIndicatorInvEmpty
                                : stylesXplrr.loadingIndicatorPreLoad
                        } `}
                    >

                        <ProgressIndicator percentComplete={progress} barHeight={30} progressHidden={progress >= 1} />
                    </div>
                    <div
                        className={`${
                            srcLoc == pdfSourceCodes.modal
                                ? stylesXplrr.loadingInfo: srcLoc == pdfSourceCodes.sbs
                                ? stylesXplrr.loadingInfoStatic: srcLoc == pdfSourceCodes.inv && file?.invoicedata != null
                                ? stylesXplrr.loadingInfoStatic: srcLoc == pdfSourceCodes.inv
                                ? stylesXplrr.loadingInfoStaticEmpty: stylesXplrr.loadingInfo
                        }`}
                    >
                        {loadingMessage}
                        {loadingMessage == "ERROR LOADING PDF" ? null : (
                            <div className={stylesXplrr.typingDots}>
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
            {!window.globalBase64String ||
                window.globalBase64String == "data:application/pdf;base64," ||
                window.globalBase64String == "" ||
                !filePageDimsState ? (
                <div style={{ minWidth: rescaleWidth }}/>
            ) : (
                <div id={`${fileId}_${rescaleWidth}`} className={stylesXplrr.pdfViewerSub} ref={containerRef}>
                    <Document
                        className={stylesXplrr.pdfViewerMainXPL}
                        file={`${window.globalBase64String}`}
                        onLoadSuccess={handleLoadSuccess}
                        options={pdfOptions}
                        noData={<div className={stylesXplrr.loadingText}>Loading...</div>}
                        loading={<div className={stylesXplrr.loadingText}>Loading...</div>}
                        onLoadError={(error: any) => { setLoadingMessage("ERROR LOADING PDF") }}
                        error={
                            <div className={stylesXplrr.errorPDFLoadSystemMsg}>
                                <div className={stylesXplrr.iconNoContent}>
                                    <MdErrorOutline size={26} />
                                </div>
                                <div>Error Loading PDF</div>
                            </div>
                        }
                    >
                        <div className={stylesXplrr.pdfDocContainer}>
                            <div key={"pdfmainid9"} className={stylesXplrr.pdfMain} id="pdfmainid9" onScroll={handleScrollCallAdv}>
                                {renderedPages}
                                <div className={stylesXplrr.pdfFooter}></div>
                            </div>
                        </div>
                    </Document>
                </div>
            )}
        </div>
    );
};

export default XplrrBOXPDF;
